import { useMemo } from "react";
import { useSelector } from "react-redux";
import { Card, Form, InputNumber, Select, Row, Col, Switch, Divider, Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { useCompOffScopeOptions } from "../hooks/useCompOffScopeOptions";
import {
	alignMultiSelectValues,
	toMultiSelectArray,
} from "../utils/scopeSelectionUtils";

export default function GeneralSettings({
	embedded = false,
	sidePanel = false,
	general,
	calculation,
	onGeneralChange,
	onCalculationChange,
}) {
	const patchGeneral = (fields) => onGeneralChange(fields);
	const patchCalculation = (fields) => onCalculationChange(fields);
	const masterData = useSelector((state) => state.masters?.masters);

	const scopeOptions = useCompOffScopeOptions();

	const calculationTypeOptions = useMemo(
		() =>
			(masterData?.CalculationComboOffTypeMaster || [])
				.filter((t) => t.DataStatus === 1)
				.map((t) => ({
					label: t.CalculationComboOffTypeName,
					value: t.CalculationComboOffTypeId,
				})),
		[masterData?.CalculationComboOffTypeMaster]
	);

	const roundTypeOptions = useMemo(
		() =>
			(masterData?.RoundComboOffTypeMaster || [])
				.filter((t) => t.DataStatus === 1)
				.map((t) => ({
					label: t.RoundComboOffTypeName,
					value: t.RoundComboOffTypeId,
				})),
		[masterData?.RoundComboOffTypeMaster]
	);

	const scopeFields = [
		{ key: "offices", label: "Office", optionsKey: "offices" },
		{ key: "departments", label: "Department", optionsKey: "departments" },
		{ key: "designations", label: "Designation", optionsKey: "designations" },
		{ key: "employeeTypes", label: "Employment Type", optionsKey: "employeeTypes" },
	];

	const eligibilityToggles = [
		{ key: "weeklyOffEligible", label: "Weekly Off" },
		{ key: "holidayEligible", label: "Holiday" },
		{ key: "overtimeEligible", label: "Overtime" },
	];

	const formContent = (
			<Form layout="vertical" className={embedded ? "compoff-embedded-form" : ""}>
				<Row gutter={[16, 0]}>
					{scopeFields.map(({ key, label, optionsKey }) => {
						const options = scopeOptions[optionsKey] || [];
						const selected = alignMultiSelectValues(
							toMultiSelectArray(general[key]),
							options
						);

						return (
						<Col xs={24} md={12} key={key}>
							<Form.Item label={`${label} (multiple)`}>
								<Select
									mode="multiple"
									allowClear
									showSearch
									optionFilterProp="label"
									filterOption={(input, option) =>
										String(option?.label ?? "")
											.toLowerCase()
											.includes(input.trim().toLowerCase())
									}
									maxTagCount="responsive"
									listHeight={280}
									loading={scopeOptions.loading}
									placeholder={`Select one or more ${label.toLowerCase()}s`}
									value={selected}
									onChange={(v) =>
										patchGeneral({
											[key]: alignMultiSelectValues(v, options),
										})
									}
									options={options}
								/>
							</Form.Item>
						</Col>
						);
					})}
				</Row>

				<Divider>Eligibility Criteria</Divider>

				<Row gutter={[16, 16]}>
					{eligibilityToggles.map(({ key, label }) => (
						<Col xs={24} sm={8} key={key}>
							<div className="compoff-toggle-row">
								<span>{label}</span>
								<Switch
									checked={general[key]}
									onChange={(v) => patchGeneral({ [key]: v })}
								/>
							</div>
						</Col>
					))}
					<Col xs={24} sm={8}>
						<Form.Item label="Minimum Hours Worked">
							<InputNumber
								min={0}
								step={0.5}
								className="w-full"
								value={general.minHoursWorked}
								onChange={(v) => patchGeneral({ minHoursWorked: v })}
							/>
						</Form.Item>
					</Col>
				</Row>

				<Divider>Combination Rules</Divider>

				<Row gutter={[16, 16]}>
					<Col xs={24} sm={12}>
						<div className="compoff-toggle-row">
							<span>Allow Combination</span>
							<Switch
								checked={general.allowCombination}
								onChange={(v) => patchGeneral({ allowCombination: v })}
							/>
						</div>
					</Col>
					<Col xs={24} sm={12}>
						<div className="compoff-toggle-row">
							<span>Combine Across Days</span>
							<Switch
								checked={general.combineAcrossDays}
								onChange={(v) => patchGeneral({ combineAcrossDays: v })}
								disabled={!general.allowCombination}
							/>
						</div>
					</Col>
					<Col xs={24} sm={12}>
						<Form.Item label="Combination Window (days)">
							<InputNumber
								min={1}
								max={30}
								className="w-full"
								value={general.combinationWindowDays}
								onChange={(v) => patchGeneral({ combinationWindowDays: v })}
								disabled={!general.allowCombination}
							/>
						</Form.Item>
					</Col>
					<Col xs={24} sm={12}>
						<Form.Item
							label={
								<span>
									Minimum Combined Hours{" "}
									<Tooltip
										title="Total work hours required across combined entries (within the combination window) before one comp-off is granted. Example: 3 hrs on Monday + 5 hrs on Tuesday = 8 hrs → 1 comp-off when minimum is 8."
									>
										<InfoCircleOutlined
											className="compoff-field-info-icon"
											aria-label="Minimum combined hours help"
										/>
									</Tooltip>
								</span>
							}
						>
							<InputNumber
								min={1}
								className="w-full"
								value={general.minCombinedHours}
								onChange={(v) => patchGeneral({ minCombinedHours: v })}
								disabled={!general.allowCombination}
							/>
						</Form.Item>
					</Col>
				</Row>

				<Divider>Calculation Rules</Divider>

				<Row gutter={[16, 16]}>
					<Col xs={24} md={12}>
						<Form.Item label="Calculation Type">
							<Select
								className="w-full"
								placeholder="Select calculation type"
								value={calculation?.calculationComboOffTypeId ?? 1}
								onChange={(v) =>
									patchCalculation({ calculationComboOffTypeId: v })
								}
								options={calculationTypeOptions}
							/>
						</Form.Item>
					</Col>
					<Col xs={24} sm={12}>
						<Form.Item label="Round Half Day" className="compoff-calculation-toggle-item">
							<div className="compoff-toggle-row compoff-toggle-row--field">
								<Switch
									checked={calculation?.roundHalfDay}
									onChange={(v) => patchCalculation({ roundHalfDay: v })}
								/>
							</div>
						</Form.Item>
					</Col>
					<Col xs={24} sm={12}>
						<Form.Item label="Round Up Logic">
							<Select
								className="w-full"
								placeholder="Select round logic"
								value={calculation?.roundComboOffTypeId ?? 1}
								onChange={(v) => patchCalculation({ roundComboOffTypeId: v })}
								options={roundTypeOptions}
							/>
						</Form.Item>
					</Col>
					<Col xs={24} sm={12}>
						<Form.Item label="Max Credit Per Day">
							<InputNumber
								min={0}
								step={0.5}
								value={calculation?.maxCreditPerDay}
								onChange={(v) => patchCalculation({ maxCreditPerDay: v })}
								className="w-full"
							/>
						</Form.Item>
					</Col>
					<Col xs={24} sm={12}>
						<Form.Item label="Max Credit Per Month">
							<InputNumber
								min={0}
								value={calculation?.maxCreditPerMonth}
								onChange={(v) => patchCalculation({ maxCreditPerMonth: v })}
								className="w-full"
							/>
						</Form.Item>
					</Col>
				</Row>
			</Form>
	);

	if (embedded) {
		return (
			<div
				className={
					sidePanel
						? "compoff-side-panel-settings"
						: "compoff-embedded-settings"
				}
			>
				{formContent}
			</div>
		);
	}

	return (
		<Card title="General Configuration" className="compoff-section-card">
			{formContent}
		</Card>
	);
}
